import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

import { CsvSeparator } from '../file-export/file-export.service';

@Injectable({ providedIn: 'root' })
export class FileImportService {
  async readSheet(
    file: File,
    separator: CsvSeparator = ',',
    opts: { cellDates?: boolean; sheetIndex?: number; raw?: boolean } = {},
  ): Promise<string[][]> {
    const cellDates = opts.cellDates ?? false;
    const sheetIndex = opts.sheetIndex ?? 0;
    // `raw: true` keeps each cell's literal text instead of letting SheetJS infer
    // numbers/dates — needed for code-like strings (e.g. waste codes "02 03 04")
    // that would otherwise be coerced into a date or numeric serial.
    const raw = opts.raw ?? false;
    const buffer = await file.arrayBuffer();
    const text = this.csvUtf8Text(file, buffer);
    const wb = XLSX.read(text ?? buffer, {
      type: text === null ? 'array' : 'string',
      FS: separator,
      cellDates,
      raw,
    });
    return XLSX.utils.sheet_to_json<string[]>(wb.Sheets[wb.SheetNames[sheetIndex]], {
      header: 1,
      defval: '',
      raw,
    });
  }

  /**
   * A UTF-8 CSV as text: SheetJS reads one without a byte-order mark as
   * Latin-1 ("Unité" -> "UnitÃ©"). Null for xlsx and non-UTF-8 CSVs
   * (Windows-1252, UTF-16), which SheetJS already reads right from the bytes.
   */
  private csvUtf8Text(file: File, buffer: ArrayBuffer): string | null {
    if (!file.name.toLowerCase().endsWith('.csv')) return null;
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
      return null;
    }
  }

  async getSheetNames(file: File): Promise<string[]> {
    const buffer: ArrayBuffer = await file.arrayBuffer();
    const workbook: XLSX.WorkBook = XLSX.read(buffer, { type: 'array' });
    return workbook.SheetNames;
  }
}
